import { useEffect, useRef, useState } from "react";

interface AnimatedTimelineProps {
  children: React.ReactNode;
  steps: number;
  /** Line on the right when true (RTL) */
  isRtl?: boolean;
}

export function AnimatedTimeline({ children, steps, isRtl }: AnimatedTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fillPercent, setFillPercent] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowH = window.innerHeight;

      const start = rect.top - windowH * 0.8;
      const end = rect.bottom - windowH * 0.3;
      const progress = Math.min(100, Math.max(0, ((0 - start) / (end - start)) * 100));
      setFillPercent(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const lineClass = isRtl ? "right-6" : "left-6";

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`absolute top-0 bottom-0 w-[2px] bg-border/40 ${lineClass}`}
        aria-hidden
      />
      <div
        className={`absolute top-0 w-[2px] bg-gradient-to-b from-primary via-primary to-primary/40 transition-[height] duration-100 ease-out ${lineClass}`}
        style={{ height: `${fillPercent}%` }}
        aria-hidden
      />
      {children}
    </div>
  );
}
